import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { IconChoices } from '../../../common/Icon'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { useModal } from '../../../../hooks/useModal'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { theme } from '../../../../theme/theme'
import { Button, ButtonVariant } from '../../../common/Button'
import { IPreApprovedList } from '../../../../interfaces/models/societies.interfaces'
import { sendPreApprovedUserInviteReminder } from '../../../../api/societies'

interface PreApprovedListItemProps {
  preApproved: IPreApprovedList
  societyId: string
}

export const PreApprovedListItem = ({
  preApproved,
  societyId,
}: PreApprovedListItemProps): React.ReactNode => {
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const {
    show: showSendReminderConfirmationModal,
    open: openSendReminderConfirmationModal,
    close: closeSendReminderConfirmationModal,
  } = useModal()

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('manageHousehold.flashMessage.userReminderSuccess')
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('manageHousehold.flashMessage.userReminderFailure')
    )
  }

  const { mutate: mutateSendPreApprovedUserInviteReminder, status } =
    useMutation({
      mutationFn: sendPreApprovedUserInviteReminder,
      onSuccess: () => {
        showSuccessMessage()
      },
      onError: (error) => {
        showErrorMessage()
        captureException(error)
      },
    })

  const sendReminder = async (email: string): Promise<void> => {
    if (societyId) {
      mutateSendPreApprovedUserInviteReminder({ id: societyId, email })
    } else {
      showErrorMessage()
    }
  }

  const buttonIcon =
    status === 'success' ? IconChoices.CHECKMARK : IconChoices.ENVELOPE

  return (
    <>
      <div className="flex items-center justify-between">
        <p style={theme.textVariants.base}>{preApproved.userEmail}</p>
        <Button
          onClick={openSendReminderConfirmationModal}
          disabled={status === 'pending'}
          icon={buttonIcon}
          loading={status === 'pending'}
          variant={ButtonVariant.PRIMARY}
        />
      </div>
      <ConfirmationModal
        title={translate('manageHousehold.reminder.confirmationModal.title')}
        description={translate(
          'manageHousehold.reminder.confirmationModal.description',
          { email: preApproved.userEmail }
        )}
        show={showSendReminderConfirmationModal}
        close={closeSendReminderConfirmationModal}
        onConfirm={() => sendReminder(preApproved.userEmail)}
        confirmationButtonLabel={translate('common.actions.send')}
      />
    </>
  )
}
